<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import KanbanMultiSelectFilter from './KanbanMultiSelectFilter.vue';
import KanbanPeriodFilter from './KanbanPeriodFilter.vue';

// PRD T3.4 secao 3: responsável, caixa de entrada, condição e período são os
// quatro filtros de uso diário e ficam sempre visíveis. Rótulos, prioridade
// e status ficam atrás de "Mais filtros" (uso semanal). SLA estourado e
// faixa de valor NÃO estão aqui: board_controller.rb#filtered_conversations
// não aceita nenhum dos dois parâmetros (nem sla_breached, nem debt_value
// min/max) — inventar um filtro client-side sobre a página já carregada
// mostraria um recorte incompleto e enganoso; ver RISCOS do retorno da
// unidade.
//
// Props explícitos (não o objeto inteiro de useKanbanBoard): `filters` é
// reactive() e propaga normalmente através de props, mas periodDays e
// hasActiveFilters são ref()s do composable — passar o objeto do composable
// inteiro faria o template ler a Ref crua em vez do valor (o unwrap
// automático do Vue só se aplica a refs de topo do próprio script setup do
// componente, não a refs aninhadas dentro de outro objeto).
const props = defineProps({
  filters: { type: Object, required: true },
  periodDays: { type: Number, required: true },
  hasActiveFilters: { type: Boolean, required: true },
  setFilter: { type: Function, required: true },
  setPeriodDays: { type: Function, required: true },
  setSearchQuery: { type: Function, required: true },
  clearFilters: { type: Function, required: true },
});

const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');

const assigneeOptions = computed(() =>
  agents.value.map(agent => ({ id: agent.id, name: agent.name }))
);
const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({ id: inbox.id, name: inbox.name }))
);
const labelOptions = computed(() =>
  labels.value.map(label => ({ id: label.title, name: label.title }))
);

const DEBT_STATE_IDS = ['ESTAGNADO', 'SRETORNO', 'CONCLUIDA', 'LIXO'];
const debtStateOptions = computed(() =>
  DEBT_STATE_IDS.map(id => ({
    id,
    name: t(`CONVERSATION.DEBT_STATE_WIDGET.STATES.${id}`),
  }))
);

const PRIORITY_IDS = ['low', 'medium', 'high', 'urgent'];
const priorityOptions = computed(() =>
  PRIORITY_IDS.map(id => ({
    id,
    name: t(`CONVERSATION.PRIORITY.OPTIONS.${id.toUpperCase()}`),
  }))
);

const STATUS_IDS = ['open', 'resolved', 'pending', 'snoozed'];
const statusOptions = computed(() =>
  STATUS_IDS.map(id => ({
    id,
    name: t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${id}.TEXT`),
  }))
);

const showMoreFilters = ref(false);
const toggleMoreFilters = () => {
  showMoreFilters.value = !showMoreFilters.value;
};

const searchInput = ref('');
const onSearchInput = () => props.setSearchQuery(searchInput.value);

// clearFilters (do composable) só zera searchQuery — searchInput é ref
// local deste componente (mesmo motivo do comentário de props acima) e sem
// isto a caixa continuava mostrando o termo digitado, com o board já sem
// filtro nenhum e o próprio botão "Clear filters" sumido (A4).
const onClearFilters = () => {
  searchInput.value = '';
  props.clearFilters();
};
</script>

<template>
  <div class="flex flex-col gap-2 px-4 py-2 border-b border-n-weak shrink-0">
    <div class="flex flex-wrap items-center gap-2">
      <KanbanMultiSelectFilter
        :label="t('KANBAN.BOARD.FILTERS.ASSIGNEE')"
        :options="assigneeOptions"
        :model-value="filters.assignees"
        @update:model-value="value => setFilter('assignees', value)"
      />
      <KanbanMultiSelectFilter
        :label="t('KANBAN.BOARD.FILTERS.INBOX')"
        :options="inboxOptions"
        :model-value="filters.inboxes"
        @update:model-value="value => setFilter('inboxes', value)"
      />
      <KanbanMultiSelectFilter
        :label="t('KANBAN.BOARD.FILTERS.CONDITION')"
        :options="debtStateOptions"
        :model-value="filters.debtStates"
        @update:model-value="value => setFilter('debtStates', value)"
      />
      <KanbanPeriodFilter
        :model-value="periodDays"
        @update:model-value="setPeriodDays"
      />

      <div class="relative flex-1 min-w-[180px] max-w-xs">
        <span
          class="i-lucide-search absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10"
          aria-hidden="true"
        />
        <!--
          ALTO (revisor-frontend, T3.4): h-8 (32px) declarado, mas
          #{$form-input-selector} de _base.scss (input[type]:not(...))
          aplica field-base h-10 (40px) por cima, sem que reset-base o
          exclua — o wrapper relative herdava os 40px reais do input, e a
          lupa (top-1/2 do wrapper) descentralizava 8px do campo, cortando o
          "S" de "Search". reset-base é o mecanismo já usado no resto do
          components-next para o mesmo caso (input com estilo próprio, não o
          de field-base).
        -->
        <input
          v-model="searchInput"
          type="text"
          class="reset-base w-full h-8 pl-7 pr-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          :placeholder="t('KANBAN.BOARD.SEARCH_PLACEHOLDER')"
          @input="onSearchInput"
        />
      </div>

      <!--
        ALTO (revisor-frontend, WCAG 2.0 nivel A, 4.1.2): mesmo padrão de
        disclosure que os outros cinco gatilhos deste board (KanbanMultiSelectFilter,
        KanbanPeriodFilter, KanbanFunnelSwitcher, o "Move to stage" do card) —
        só não tinha aria-expanded porque a medição anterior olhou só quem
        abre um popup flutuante; este alterna showMoreFilters igual aos
        outros, mostrando/escondendo conteúdo inline. aria-haspopup não entra
        aqui: o conteúdo revelado não é um popup sobreposto, é uma segunda
        linha de filtros no fluxo normal do documento.
      -->
      <NextButton
        slate
        ghost
        xs
        trailing-icon
        :icon="
          showMoreFilters ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
        "
        :label="
          showMoreFilters
            ? t('KANBAN.BOARD.FEWER_FILTERS')
            : t('KANBAN.BOARD.MORE_FILTERS')
        "
        :aria-expanded="showMoreFilters"
        @click="toggleMoreFilters"
      />

      <NextButton
        v-if="hasActiveFilters"
        slate
        ghost
        xs
        icon="i-lucide-x"
        :label="t('KANBAN.BOARD.CLEAR_FILTERS')"
        @click="onClearFilters"
      />
    </div>

    <div v-if="showMoreFilters" class="flex flex-wrap items-center gap-2">
      <KanbanMultiSelectFilter
        :label="t('KANBAN.BOARD.FILTERS.LABELS')"
        :options="labelOptions"
        :model-value="filters.labels"
        @update:model-value="value => setFilter('labels', value)"
      />
      <KanbanMultiSelectFilter
        :label="t('KANBAN.BOARD.FILTERS.PRIORITY')"
        :options="priorityOptions"
        :model-value="filters.priorities"
        @update:model-value="value => setFilter('priorities', value)"
      />
      <KanbanMultiSelectFilter
        :label="t('KANBAN.BOARD.FILTERS.STATUS')"
        :options="statusOptions"
        :model-value="filters.statuses"
        @update:model-value="value => setFilter('statuses', value)"
      />
    </div>
  </div>
</template>
